import { Spinner } from '@/components/ui/spinner';
import { FunctionLetter } from '@/components/words/function-letter';
import { RoundTagGroup } from '@/components/words/round-tag-group';
import { ToCheckBadge } from '@/components/words/to-check-badge';
import { useTweak } from '@/lib/feature-flags';
import { NOYAU_KEY_VARS, displayNoyauPonderation } from '@/lib/noyaux';
import { useResultViewMode } from '@/lib/result-view-mode';
import { useWordsNav } from '@/lib/words-nav';
import { type CSSProperties, useEffect, useRef } from 'react';

/** Desktop-only card-grid alternative to the sidebar list, showing the same results. */
export function WordsMosaic() {
  const nav = useWordsNav();
  const [, setViewMode] = useResultViewMode();
  const cardStyle = useTweak('mosaic_card_style') === 'border' ? 'border' : 'tint';
  // Infinite scroll: load the next page once the container is scrolled near
  // its bottom (plain scroll-position check, more reliable than an
  // IntersectionObserver sentinel across layouts — see words-sidebar.tsx).
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = nav;
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onScroll = () => {
      if (!hasNextPage || isFetchingNextPage) return;
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        fetchNextPage();
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (nav.isLoading) {
    return (
      <div className="words-mosaic__loading">
        <Spinner size="sm" label="Chargement des résultats" />
      </div>
    );
  }

  if (nav.results.length === 0) {
    return <p className="words-empty">Aucun résultat</p>;
  }

  return (
    <div className="words-mosaic-wrap" ref={wrapRef}>
      <div className="words-mosaic" aria-label="Résultats de recherche (mosaïque)">
        {nav.results.map((word) => {
          const vars = word.dominantNoyauKey ? NOYAU_KEY_VARS[word.dominantNoyauKey] : null;
          const accentStyle = vars
            ? ({
                '--card-accent': vars.base,
                '--card-accent-tint': vars.tint,
              } as CSSProperties)
            : undefined;
          return (
            <button
              key={word.id}
              type="button"
              className={`words-mosaic__card words-mosaic__card--${cardStyle}`}
              style={accentStyle}
              onClick={() => {
                setViewMode('list');
                nav.pushWord(word.id);
              }}
            >
              <span className="words-mosaic__title-row">
                <span className="words-mosaic__title">{word.title}</span>
                <ToCheckBadge toCheck={word.toCheck} reasons={word.reasons} />
                <FunctionLetter fonction={word.fonction} placeholder />
              </span>
              <span className="words-mosaic__meta">
                <RoundTagGroup
                  noyauPonderation={displayNoyauPonderation(
                    word.noyauPonderation,
                    word.dominantNoyauCode ?? null,
                  )}
                  size="compact"
                />
              </span>
            </button>
          );
        })}
      </div>
      {nav.capped ? (
        <p className="words-results__more">Affinez la recherche pour voir plus de résultats.</p>
      ) : (
        nav.isFetchingNextPage && (
          <div className="words-results__sentinel" aria-hidden="true">
            <Spinner size="sm" label="Chargement…" />
          </div>
        )
      )}
    </div>
  );
}
